import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { ACCESS_WARNING_DAYS, getMyAccess } from '../../services/accessService';
import type { MyAccess } from '../../services/accessService';
import { formatEndDate } from '../../utils/accessDate';

/**
 * Prazo de acesso no painel do aluno: discreto (TEXTO 1) e, a partir de 30
 * dias do fim, em destaque de aviso (TEXTO 2). Sem data final, nada.
 *
 * Quem decide o que mostrar é o backend: com o bloqueio por vencimento ainda
 * desligado (`enforced: false`), a data não vem, porque pode mudar na revisão.
 * Qualquer erro também não mostra nada: é um aviso, não um gate.
 */
const AccessExpiryBanner: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();

  const { data, isError } = useQuery<MyAccess>({
    // Por conta: cada curso tem a sua data.
    queryKey: ['my-access', user?.id],
    queryFn: getMyAccess,
    // Para admin o backend não manda data.
    enabled: isAuthenticated && !!user,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

  if (isError || !data?.enforced || !data.accessEndDate || data.daysLeft === null) return null;

  const date = formatEndDate(data.accessEndDate);

  if (data.daysLeft > ACCESS_WARNING_DAYS) {
    return (
      <p className="access-banner" data-testid="access-banner">
        <i className="fas fa-calendar-check" aria-hidden="true"></i>
        {t('access.bannerUntil', { date })}
      </p>
    );
  }

  return (
    <div
      role="status"
      data-testid="access-banner"
      className="access-banner access-banner--warning"
    >
      <i className="fas fa-hourglass-end" aria-hidden="true"></i>
      {data.daysLeft === 0
        ? t('access.bannerEndsToday', { date })
        : t('access.bannerEnding', { count: data.daysLeft, date })}
    </div>
  );
};

export default AccessExpiryBanner;
